/**
 * Entity Resolver - Central service for resolving llmId to entity data
 * 
 * This service implements a two-tier resolution strategy:
 * 1. PRIMARY: Fast cache lookup in spaceStore.windows (CRDT-synced)
 * 2. FALLBACK: Server fetch via entityService for entities not currently open
 * 
 * This design allows commands to work with llmId while leveraging our existing
 * CRDT infrastructure for performance.
 */

import useSpaceStore from '../stores/spaceStore';
import { entityService } from './entityService';
import { createFileLogger } from '../utils/logger';
import { llmIdMapper } from '../utils/llmIdMapper';
import { CommandExecutionContext } from '../types/commands';
import { AnyWindowEntity } from '../types/windows';
import { readAll as readAllDoc } from '../apps/DocsApp/services/doc/docApi';
import type { CommandContext as DocumentCommandContext } from '../apps/DocsApp/services/doc/internal/yDocHighLevelAPI';
import { docsCRDTService } from '../apps/DocsApp/services/crdt/docsCRDTService';
import { getDocsAppStore } from '../apps/DocsApp/store/docsAppStore';

const log = createFileLogger('ENTITY_RESOLVER');

export interface ResolvedEntity {
  entity: any;
  windowId?: string;      // Present if entity is open in a window
  windowEntity?: AnyWindowEntity;  // Full window object if found
  source: 'cache' | 'server';     // Where the data came from
}

export interface DocumentContext {
  entity: any;
  windowId?: string;
  ydoc?: any;            // YJS document if this is a docs entity
  docContext?: any;      // Full document context for docs operations
  source?: 'cache' | 'server';  // Where the data came from
}

class EntityResolver {
  /**
   * Resolve an llmId to entity data, preferring local cache
   */
  async resolveEntity(llmId: string, context: CommandExecutionContext): Promise<ResolvedEntity> {
    // 🛡️ LAYER 1: For Doc entities, always fetch fresh from backend
    // EntityStore has metadata (base64/content stripped), so we must fetch full doc
    const cachedEntity = await this.checkIfDocEntity(llmId);
    if (cachedEntity?.entityType === 'Doc') {
      console.log(`[EntityResolver] 📄 Doc entity detected - fetching fresh from backend`);
      return await this.resolveDocFresh(llmId, cachedEntity);
    }
    
    // STEP 1: Fast cache lookup in spaceStore.windows (non-Doc entities only)
    const cacheResult = this.resolveFromCache(llmId);
    if (cacheResult) {
      return cacheResult;
    }
    
    // STEP 1.5: 🚨 NEW: Check documentReferences for entities not in windows
    const documentReferences = (context as any)?.documentReferences || [];
    console.log('[resolveEntity] 🔍 Checking documentReferences:', JSON.stringify({
      llmId,
      hasContext: !!context,
      documentReferencesCount: documentReferences.length,
      documentReferences: documentReferences.map((d: any) => ({
        llmId: d.llmId,
        title: d.title,
        actualId: d.actualId
      }))
    }, null, 2));
    
    const docRef = documentReferences.find((ref: any) => ref.llmId === llmId);
    if (docRef) {
      log.log('[resolveEntity] ✅ Found in documentReferences:', { llmId, title: docRef.title });
      
      // ✅ PERFORMANCE FIX: Check EntityStore BEFORE making API call
      try {
        const useEntityStore = (await import('../stores/entityStore')).default;
        const entityStore = useEntityStore.getState();
        
        console.log('[resolveEntity] 🔍 Checking EntityStore:', JSON.stringify({
          llmId,
          totalEntities: entityStore.entities.length,
          currentSpaceId: entityStore.currentSpaceId,
          docRefActualId: docRef.actualId,
          searchingFor: {
            byLlmId: llmId,
            byActualId: docRef.actualId,
            byId: llmId
          }
        }, null, 2));
        
        // Try to find by llmId first
        let entity = entityStore.entities.find((e: any) => e.llmId === llmId);
        
        // Fallback: try to find by _id if llmId didn't work
        if (!entity) {
          entity = entityStore.entities.find((e: any) => e._id === llmId || e.id === llmId);
        }
        
        // Try docRef.actualId if available
        if (!entity && docRef.actualId) {
          entity = entityStore.entities.find((e: any) => 
            e._id === docRef.actualId || e.id === docRef.actualId || e.llmId === docRef.actualId
          );
          console.log('[resolveEntity] 🔍 Tried docRef.actualId:', JSON.stringify({
            actualId: docRef.actualId,
            found: !!entity
          }, null, 2));
        }
        
        if (entity) {
          // 🛡️ CRITICAL FIX: Don't use cache for Doc entities (bypass protection)
          if (entity.entityType === 'Doc') {
            console.warn('[resolveEntity] ⚠️ Doc entity found in cache, fetching fresh to ensure content');
            const freshEntity = await entityService.get(llmId);
            return {
              entity: freshEntity,
              windowId: docRef.windowId,
              source: 'server'
            };
          }
          
          console.log('[resolveEntity] ✅ Found in EntityStore cache (no API call!):', JSON.stringify({
            llmId,
            entityId: entity._id,
            entityType: entity.entityType,
            source: 'EntityStore'
          }, null, 2));
          
          return {
            entity,
            windowId: docRef.windowId,
            source: 'cache'
          };
        }
        
        console.log('[resolveEntity] ⚠️ Not found in EntityStore, falling back to API', JSON.stringify({
          llmId,
          docRefActualId: docRef.actualId,
          willFetchFromAPI: true
        }, null, 2));
      } catch (error: any) {
        console.warn('[resolveEntity] EntityStore lookup failed, falling back to API:', JSON.stringify({
          error: error.message,
          stack: error.stack
        }, null, 2));
      }
      
      // Fallback: Fetch the entity from server if not in EntityStore
      try {
        console.log('[resolveEntity] 📡 Fetching entity from API:', JSON.stringify({
          llmId,
          docRefActualId: docRef.actualId,
          willTryLlmId: llmId,
          willTryActualId: docRef.actualId
        }, null, 2));
        
        // Try with actualId first if available
        const entityIdToFetch = docRef.actualId || llmId;
        const entity = await entityService.get(entityIdToFetch);
        
        if (entity) {
          console.log('[resolveEntity] ✅ Entity fetched from API:', JSON.stringify({
            fetchedWith: entityIdToFetch,
            entityId: entity._id,
            entityLlmId: entity.llmId,
            entityType: entity.entityType,
            entityName: entity.name
          }, null, 2));
          
          return {
            entity,
            windowId: docRef.windowId,
            source: 'server'
          };
        }
      } catch (error: any) {
        console.warn('[resolveEntity] Failed to fetch entity from documentReferences:', JSON.stringify({
          error: error.message,
          llmId,
          docRefActualId: docRef.actualId
        }, null, 2));
      }
    }
    
    // STEP 1.7: ✅ PERFORMANCE FIX: Check EntityStore before server fetch
    try {
      const useEntityStore = (await import('../stores/entityStore')).default;
      const entityStore = useEntityStore.getState();
      
      // Try to find by llmId first
      let entity = entityStore.entities.find((e: any) => e.llmId === llmId);
      
      // Fallback: try to find by _id if llmId didn't work
      if (!entity) {
        entity = entityStore.entities.find((e: any) => e._id === llmId || e.id === llmId);
      }
      
      if (entity) {
        // 🛡️ CRITICAL FIX: Don't use cache for Doc entities (bypass protection)
        if (entity.entityType === 'Doc') {
          console.warn('[resolveEntity] ⚠️ Doc entity found in EntityStore, fetching fresh to ensure content');
          return await this.fetchFreshDocWithFallback(llmId, entity);
        }
        
        log.log('[resolveEntity] ✅ Found in EntityStore cache (no API call!):', {
          llmId,
          entityId: entity._id,
          entityType: entity.entityType,
          source: 'EntityStore'
        });
        
        return {
          entity,
          source: 'cache'
        };
      }
      
      log.log('[resolveEntity] ⚠️ Not found in EntityStore, falling back to server');
    } catch (error) {
      log.warn('[resolveEntity] EntityStore lookup failed, falling back to server:', error);
    }
    
    // STEP 2: Fallback to server fetch
    log.log('[resolveEntity] ⚠️ Not found in cache, falling back to server');
    return await this.resolveFromServer(llmId);
  }

  /**
   * Specialized resolver for document entities (includes YJS context)
   */
  async resolveDoc(llmId: string, context: CommandExecutionContext): Promise<DocumentContext> {
    const resolved = await this.resolveEntity(llmId, context);
    
    if (!resolved.entity) {
      throw new Error(`Entity with llmId "${llmId}" not found`);
    }

    // For docs entities, we need additional context
    if (resolved.entity.entityType === 'Doc' && resolved.windowId) {
      try {
        // ✅ ENTITY-FIRST: Try spaceStore first, fallback to service layer
        let ydoc = null;
        let docId = resolved.entity._id || resolved.entity.id;
        let docContext = null;
        
        // Try spaceStore first (if document is open)
        const { windows } = useSpaceStore.getState();
        const window = windows[resolved.windowId];
        const docsState = (window?.applicationState as any)?.docs;
        if (docsState?.ydoc) {
          ydoc = docsState.ydoc;
          docId = docsState.activeDocId || docId;
          log.log('[entityResolver] Using open document from spaceStore');
        }

        // ✅ NEW: Fallback to DocsApp store binding for the active window/space
        if (!ydoc && context.spaceId) {
          try {
            const store = getDocsAppStore(resolved.windowId, String(context.spaceId));
            const state = store.getState();
            if (state?.ydoc) {
              ydoc = state.ydoc;
              docId = state.docId || docId;
              log.log('[entityResolver] Using Y.Doc from DocsAppStore');
            }
          } catch (e) {
            log.warn('[entityResolver] DocsAppStore lookup failed:', e);
          }
        }
        
        // Fallback to service layer (if not open)
        if (!ydoc) {
          try {
            const { doc } = await docsCRDTService.getYDocForDoc(docId);
            ydoc = doc;
            
            const tempContext: DocumentCommandContext = {
              yxml: ydoc.getXmlFragment('prosemirror'),
              doc: ydoc,
              docId: docId,
              userId: 'current-user',
              metadata: {
                windowId: resolved.windowId,
                spaceId: context.spaceId
              }
            };
            
            const readResult = readAllDoc(tempContext as any);
            
            if (readResult.nodes) {
              docContext = {
                ...tempContext,
                nodes: readResult.nodes
              };
            }
            log.log('[entityResolver] Using document from service layer');
          } catch (error) {
            log.warn('[entityResolver] Could not access document from service layer:', error);
          }
        }
        
        if (ydoc && !docContext) { // Check if docContext was already created
          // Create context using the retrieved ydoc
          docContext = {
              yxml: ydoc.getXmlFragment('prosemirror'),
              doc: ydoc,
              docId: docId,
              userId: 'current-user',
              metadata: {
                  windowId: resolved.windowId,
                  spaceId: context.spaceId
              }
          };
        }
        
        return {
          entity: resolved.entity,
          windowId: resolved.windowId,
          ydoc: ydoc,
          docContext,
          source: resolved.source
        };
      } catch (error) {
        log.warn('[resolveDoc] Failed to create YJS context, returning basic entity:', error);
        return {
          entity: resolved.entity,
          windowId: resolved.windowId,
          source: resolved.source
        };
      }
    }
    
    return {
      entity: resolved.entity,
      windowId: resolved.windowId,
      source: resolved.source
    };
  }

  /**
   * FAST PATH: Search through open windows for entity with matching llmId
   * Uses normalized entity storage
   */
  private resolveFromCache(llmId: string): ResolvedEntity | null {
    const spaceStore = useSpaceStore.getState();
    
    // ✅ CRITICAL FIX: Handle window ID vs entity ID confusion
    // If the llmId looks like a window ID, try to get the actual entity from that window
    if (llmId.startsWith('window-')) {
      log.log('[resolveFromCache] Detected window ID, attempting to resolve to entity:', llmId);
      
      const window = spaceStore.windows[llmId];
      if (window) {
        const windowEntity = spaceStore.getWindowEntity(llmId);
        if (windowEntity && (windowEntity.llmId || windowEntity._id)) {
          log.log('[resolveFromCache] ✅ Resolved window ID to entity:', {
            windowId: llmId,
            entityLlmId: windowEntity.llmId,
            entityId: windowEntity._id,
            entityName: windowEntity.name
          });
          
          return {
            entity: windowEntity,
            windowId: llmId,
            windowEntity: window,
            source: 'cache'
          };
        } else {
          log.log('[resolveFromCache] ❌ Window exists but has no valid entity:', llmId);
          return null;
        }
      }
    }
    
    // ✅ REFACTOR: Always use entityStore as single source of truth
    const useEntityStore = require('../stores/entityStore').default;
    const entityStore = useEntityStore.getState();
    
    console.log('[resolveFromCache] 🔍 EntityStore state:', JSON.stringify({
      llmId,
      totalEntities: entityStore.entities.length,
      currentSpaceId: entityStore.currentSpaceId,
      entityTypes: Array.from(new Set(entityStore.entities.map((e: any) => e.entityType))),
      entitySpaceIds: Array.from(new Set(entityStore.entities.map((e: any) => e.spaceId)))
    }, null, 2));
    
    // Try to find by llmId first
    let entity = entityStore.entities.find((e: any) => e.llmId === llmId);
    
    // Fallback: try to find by _id if llmId didn't work
    if (!entity) {
      entity = entityStore.entities.find((e: any) => e._id === llmId || e.id === llmId);
    }
    
    if (!entity) {
      console.log('[resolveFromCache] ❌ Cache miss for llmId:', JSON.stringify({
        llmId,
        totalEntities: entityStore.entities.length,
        sampleEntities: entityStore.entities.slice(0, 3).map((e: any) => ({
          _id: e._id,
          llmId: e.llmId,
          entityType: e.entityType,
          spaceId: e.spaceId
        }))
      }, null, 2));
      return null;
    }
    
    log.log('[resolveFromCache] ✅ Found in EntityStore:', {
      llmId,
      entityId: entity._id,
      entityType: entity.entityType
    });
    
    // Find the window that references this entity
    let foundWindowId: string | undefined;
    let foundWindowEntity: any;
    for (const [windowId, windowEntity] of Object.entries(spaceStore.windows)) {
      if (windowEntity.entityLlmId === llmId) {
        foundWindowId = windowId;
        foundWindowEntity = spaceStore.getWindowWithEntity(windowId); // Get backward compatible format
        break;
      }
    }
    
    log.log('[resolveFromCache] ✅ Cache hit:', { 
      llmId, 
      windowId: foundWindowId,
      entityType: entity.entityType,
      entityName: entity.name,
      foundInWindow: !!foundWindowId
    });
    
    return {
      entity,
      windowId: foundWindowId,
      windowEntity: foundWindowEntity,
      source: 'cache'
    };
  }

  /**
   * FALLBACK PATH: Fetch entity from server using llmIdMapper
   */
  private async resolveFromServer(llmId: string): Promise<ResolvedEntity> {
    try {
      // ✅ CRITICAL FIX: Don't try to fetch window IDs from server
      if (llmId.startsWith('window-')) {
        log.error('[resolveFromServer] ❌ Cannot fetch window ID from server:', llmId);
        throw new Error(`Cannot resolve window ID "${llmId}" as an entity. Window IDs are not valid entity identifiers.`);
      }
      
      // Try to resolve llmId to actualId using the mapper
      const actualId = llmIdMapper.getActualId(llmId);
      
      if (!actualId || actualId.startsWith('pending-')) {
        // Handle pending mappings - don't try to fetch pending entities
        if (actualId && actualId.startsWith('pending-')) {
          log.log('[resolveFromServer] Found pending mapping, entity not yet created:', { llmId, actualId });
          throw new Error(`Entity with llmId "${llmId}" is still being created. Please try again in a moment.`);
        }
        
        // If not in mapper, try direct fetch (maybe the llmId IS the actual ID)
        log.log('[resolveFromServer] No mapping found, trying direct fetch for llmId:', llmId);
        
        const entity = await entityService.get(llmId);
        
        // ✅ ENHANCED: Validate entity has proper ID before registering
        const entityId = entity._id || entity.id;
        if (!entityId) {
          log.error('[resolveFromServer] ❌ Entity fetched but has no ID:', {
            llmId,
            entity: JSON.stringify(entity),
            hasId: !!entity.id,
            has_id: !!entity._id
          });
          throw new Error(`Entity "${llmId}" fetched from server but has no valid ID field`);
        }
        
        // Register the mapping for future use
        llmIdMapper.registerMapping(
          llmId, 
          entityId, 
          'document', 
          entity.entityType, 
          entity.name
        );
        
        // ✅ CRITICAL FIX: Also register the reverse mapping if entity has an llmId
        // This ensures that if we fetch by entity._id, we also map the llmId correctly
        if (entity.llmId && entity.llmId !== llmId) {
          llmIdMapper.registerMapping(
            entity.llmId,
            entityId,
            'document',
            entity.entityType,
            entity.name
          );
          log.log('[resolveFromServer] ✅ Registered entity.llmId mapping:', {
            entityLlmId: entity.llmId,
            entityId,
            reason: 'ensure_bidirectional_mapping'
          });
        }
        
        log.log('[resolveFromServer] ✅ Direct fetch successful:', {
          llmId,
          entityId,
          entityLlmId: entity.llmId,
          entityType: entity.entityType,
          hasValidId: !!entityId
        });
        
        return {
          entity,
          source: 'server'
        };
      }
      
      // Use the mapped actualId to fetch from server
      const entity = await entityService.get(actualId);
      
      log.log('[resolveFromServer] ✅ Server fetch successful:', { 
        llmId, 
        actualId, 
        entityType: entity.entityType 
      });
      
      return {
        entity,
        source: 'server'
      };
      
    } catch (error) {
      log.error('[resolveFromServer] ❌ Server fetch failed:', error);
      throw new Error(`Failed to resolve entity for llmId "${llmId}": ${error instanceof Error ? error.message : String(error)}`);
    }
  }





  /**
   * DEPRECATED: Use useSpaceStore.getState().getWorkspaceEntities() instead
   * Kept for backward compatibility during transition
   */
  getOpenEntities(): Array<{ llmId: string; name: string; type: string; windowId: string }> {
    log.warn('[getOpenEntities] DEPRECATED: Use useSpaceStore.getState().getWorkspaceEntities() instead');
    return useSpaceStore.getState().getWorkspaceEntities().map(({ entity, ...rest }) => rest);
  }

  /**
   * 🛡️ LAYER 1 HELPER: Check if llmId corresponds to a Doc entity (fast cache check only)
   */
  private async checkIfDocEntity(llmId: string): Promise<any | null> {
    try {
      const useEntityStore = (await import('../stores/entityStore')).default;
      const entityStore = useEntityStore.getState();
      
      let entity = entityStore.entities.find((e: any) => e.llmId === llmId);
      if (!entity) {
        entity = entityStore.entities.find((e: any) => e._id === llmId || e.id === llmId);
      }
      
      return entity || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Resolve document by fetching fresh from backend
   * Always fetches latest version (no caching - per architecture decision)
   */
  private async resolveDocFresh(llmId: string, metadata: any): Promise<ResolvedEntity> {
    const docId = metadata._id || metadata.id;
    
    log.log('[resolveDocFresh] Fetching fresh document from backend', {
      docId,
      llmId,
      docName: metadata.name
    });
    
    // Always fetch fresh from backend
    return await this.fetchFreshDocWithFallback(llmId, metadata);
  }

  /**
   * Fetch fresh Doc entity from backend (single source of truth)
   * No caching - always returns fresh data
   */
  private async fetchFreshDocWithFallback(llmId: string, cachedEntity: any): Promise<ResolvedEntity> {
    // Fetch fresh from backend API
    const entity = await entityService.get(llmId);
    
    if (!entity) {
      throw new Error(`Document ${llmId} not found`);
    }
    
    const docId = entity._id || entity.id;
    log.log('[fetchFreshDoc] Entity fetched from backend', {
      docId: docId.slice(-8),
      hasBase64: !!entity.skeleton?.base64,
      base64Length: entity.skeleton?.base64?.length || 0,
      name: entity.name
    });
    
    return {
      entity,
      source: 'server'
    };
  }
}

// Export singleton instance
export const entityResolver = new EntityResolver();

// Default export for convenience
export default entityResolver;